import axios from "axios";
import {
  Import,
  ImportType,
  type JsxElement,
  JsxElementType,
} from "./jsxElementTypes";
import type { NavTreeNode } from "../types";
import { toast } from "sonner";
import { ReactFramework } from "../templateProjects";

import babelTraverse from "@babel/traverse";
import * as t from "@babel/types";
import { parse, print } from "recast";
import * as recastBabelTSParser from "recast/parsers/babel-ts";
import { convertValueToASTNode, ensureImportInAST } from "./astUtils";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";

export const DATA_TEMPO_PREV_CODEBASE_ID = "data-tempo-prev-codebase-id";

const EMPTY_COMPONENT_TEMPLATE = `import React from "react";

const ExtractedComponent = () => {
  return <></>;
};

export default ExtractedComponent;`;

// Matches in constructNewComponent.tsx in tempo-api
export enum ExtractedPropType {
  LITERAL = "LITERAL",
  FUNCTION = "FUNCTION",
  JSON_OBJECT = "JSON_OBJECT",
}

export type ExtractedProp = {
  type: ExtractedPropType;
  name: string;
  value: unknown;
};

export enum ExtractedElementType {
  JSX = "JSX",
  LITERAL = "LITERAL",
  FRAGMENT = "FRAGMENT",
}

export type ExtractedCodebaseInfo = {
  jsxName?: string;
  componentSourceImport?: Import;
  codebaseId?: string;
};

export type ExtractedElement = {
  type: ExtractedElementType;

  // One of these two must be set
  literalValue?: unknown;
  codebaseInfo?: {
    jsxName?: string;
    componentSourceImport?: Import;
    codebaseId?: string;
  };

  props?: ExtractedProp[];
  children?: ExtractedElement[];
};

const getExtractedElement = (
  navTreeNode: NavTreeNode,
  treeElementLookup: { [codebaseId: string]: JsxElement },
  forDetach?: boolean,
): {
  extractedElement?: ExtractedElement;
  sourceFilePath?: string;
  error?: string;
} => {
  if (!navTreeNode?.tempoElement?.codebaseId) {
    return {
      error: "No codebase element found for selected node",
    };
  }

  const fileForNavTreeNode =
    treeElementLookup[navTreeNode.tempoElement.codebaseId]?.filename;
  if (!fileForNavTreeNode && !forDetach) {
    return {
      error: "No filename found for selected node",
    };
  }

  const getExtractedElementForNode = (
    node: NavTreeNode,
  ): ExtractedElement | null => {
    const treeElementForNode = treeElementLookup[node.tempoElement.codebaseId];

    // In non-detach mode, filter for only components in the same file
    if (
      (!treeElementForNode ||
        treeElementForNode.filename !== fileForNavTreeNode) &&
      !forDetach
    ) {
      return null;
    }

    const props: ExtractedProp[] = [];
    const propsFromSetProps = new Set();

    // Do not include props for components that are being detached (only from DOM)
    if (!(forDetach && node.isComponent)) {
      Object.keys(treeElementForNode?.setProps || {}).forEach((propName) => {
        if (treeElementForNode.setProps?.[propName].value) {
          props.push({
            name: propName,
            type: ExtractedPropType.LITERAL,
            value: treeElementForNode.setProps?.[propName].value,
          });
          propsFromSetProps.add(propName);
        }
      });
    }

    let codebaseInfo: ExtractedCodebaseInfo = treeElementForNode;
    if (forDetach && node.isComponent && node.elementTagName) {
      codebaseInfo = {
        jsxName: node.elementTagName.toLowerCase(),
      };
    }

    let newElement: ExtractedElement = {
      type: ExtractedElementType.JSX,
      codebaseInfo,
      props: props.concat(
        Object.keys(node.props || {})
          .filter((propName) => {
            // Return any props that are set but are not literal values that we know
            // For detach, return all props we've extracted
            return (
              (treeElementForNode?.setProps?.[propName] || forDetach) &&
              !propsFromSetProps.has(propName)
            );
          })
          .map((propName) => {
            const prop = node.props?.[propName];
            if (!prop) {
              return;
            }

            return {
              name: propName,
              type: prop.type,
              value: prop.value,
            };
          })
          .filter((prop) => prop !== undefined),
      ),
      children: [],
    };

    const childrenToProcess = [...node.children];
    while (childrenToProcess.length) {
      const child = childrenToProcess.shift();
      if (!child) continue;
      if (forDetach && child?.isComponent && !child.elementTagName) {
        childrenToProcess.push(...child.children);
        continue;
      }

      const extractedChild = getExtractedElementForNode(child);
      if (extractedChild) {
        newElement?.children?.push(extractedChild);
      } else if (child.children?.length) {
        childrenToProcess.push(...child.children);
      }
    }

    if (node.literalChildren) {
      for (const literalChild of node.literalChildren.sort(
        (a, b) => a.index - b.index,
      )) {
        newElement?.children?.splice(literalChild.index, 0, {
          type: ExtractedElementType.LITERAL,
          literalValue: literalChild.value,
        });
      }
    }

    // Remove any null children
    newElement.children = newElement?.children?.filter((child) => child);

    // This is likely a component defined in the same file, skip it and just add children in a fragment
    if (
      !treeElementForNode?.componentSourceImport &&
      treeElementForNode?.type === JsxElementType.ComponentInstance
    ) {
      newElement = {
        type: ExtractedElementType.FRAGMENT,
        children: newElement.children,
      };
    }

    return newElement;
  };

  let extractedElement = getExtractedElementForNode(navTreeNode);

  // The top level node is a component that does not have a forward ref, so take it's children
  if (forDetach && navTreeNode?.isComponent && !navTreeNode.elementTagName) {
    if (!extractedElement?.children?.length) {
      return {
        error: "No html elements to extract",
      };
    }

    // Special case - component has mutliple top-level children, wrap them in a div
    if (extractedElement?.children?.length > 1) {
      extractedElement = {
        type: ExtractedElementType.JSX,
        codebaseInfo: {
          jsxName: "div",
        },
        props: [
          {
            name: "className",
            type: ExtractedPropType.LITERAL,
            value: "w-full h-full",
          },
        ],
        children: extractedElement?.children,
      };
    } else {
      extractedElement = extractedElement?.children?.[0];
    }
  }

  return {
    extractedElement: extractedElement ?? undefined,
    sourceFilePath: fileForNavTreeNode,
  };
};

export const constructNewComponent = (
  canvasId: string,
  extractedElements: ExtractedElement[],
  sourceFilePath: string | null,
): {
  newFileContents: string;
  dummyFilePath: string | null;
} => {
  const importsToAdd: Import[] = [];

  try {
    const getJSXNodeForElement = (
      element: ExtractedElement,
    ): t.JSXElement | t.JSXText | t.JSXFragment => {
      if (!element) {
        // @ts-expect-error TS2322
        return null;
      }

      if (element.type === ExtractedElementType.LITERAL) {
        return t.jsxText((element.literalValue || "").toString());
      }

      if (element.type === ExtractedElementType.FRAGMENT) {
        const newJSXElement = t.jsxFragment(
          t.jsxOpeningFragment(),
          t.jsxClosingFragment(),
          [],
        );

        // @ts-expect-error TS18048
        element.children.forEach((child) => {
          newJSXElement.children.push(getJSXNodeForElement(child));
        });

        newJSXElement.children = newJSXElement.children.filter(
          (child) => child,
        );

        return newJSXElement;
      }

      if (!element.codebaseInfo) {
        throw new Error("codebaseInfo is required for JSX elements");
      }

      const nodeName = element.codebaseInfo.jsxName || "div";
      const componentImport = element.codebaseInfo.componentSourceImport;
      const prevCodebaseId = element.codebaseInfo.codebaseId;

      if (componentImport) {
        importsToAdd.push(componentImport);
      }

      const allProps =
        element.props
          ?.map((prop) => {
            switch (prop.type) {
              case ExtractedPropType.LITERAL:
                return t.jsxAttribute(
                  t.jsxIdentifier(prop.name),
                  t.jsxExpressionContainer(convertValueToASTNode(prop.value)),
                );
              case ExtractedPropType.FUNCTION:
                // Replaces the function with a console.log
                // E.g. onClick={() => console.log("Hello")}
                return t.jsxAttribute(
                  t.jsxIdentifier(prop.name),
                  t.jsxExpressionContainer(
                    t.arrowFunctionExpression(
                      [],
                      t.callExpression(
                        t.memberExpression(
                          t.identifier("console"),
                          t.identifier("log"),
                        ),
                        [t.stringLiteral((prop.value as string).toString())],
                      ),
                    ),
                  ),
                );
              case ExtractedPropType.JSON_OBJECT:
                return t.jsxAttribute(
                  t.jsxIdentifier(prop.name),
                  t.jsxExpressionContainer(
                    convertValueToASTNode(
                      JSON.parse((prop.value as string).toString()),
                    ),
                  ),
                );
            }
          })
          .filter((prop) => prop) || [];

      if (prevCodebaseId) {
        allProps.push(
          t.jsxAttribute(
            t.jsxIdentifier(DATA_TEMPO_PREV_CODEBASE_ID),
            t.jsxExpressionContainer(t.stringLiteral(prevCodebaseId)),
          ),
        );
      }

      const selfClosing = Boolean(!element.children?.length);

      const newJSXElement = t.jsxElement(
        t.jsxOpeningElement(t.jsxIdentifier(nodeName), allProps, selfClosing),
        selfClosing ? null : t.jsxClosingElement(t.jsxIdentifier(nodeName)),
        [],
        selfClosing,
      );

      element.children?.forEach((child) => {
        newJSXElement.children.push(getJSXNodeForElement(child));
      });

      newJSXElement.children = newJSXElement.children.filter((child) => child);

      return newJSXElement;
    };

    const newJSXElements = extractedElements
      .map((el) => getJSXNodeForElement(el))
      .filter((el) => el);

    if (!newJSXElements?.length) {
      throw new Error("Could not find any JSX elements in extracted code");
    }

    // Create a new componet with the extracted elements
    const emptyComponentAST = parse(EMPTY_COMPONENT_TEMPLATE, {
      parser: recastBabelTSParser,
    });

    let foundFragment = false;
    babelTraverse(emptyComponentAST, {
      // TODO: Typing needs to be fixed for the path
      JSXFragment(babelPath: any) {
        if (foundFragment) {
          return;
        }

        foundFragment = true;
        if (newJSXElements.length === 1) {
          // @ts-ignore
          babelPath.replaceWith(newJSXElements[0]);
        } else {
          // Set the new jsx elements as the children
          babelPath.node.children = newJSXElements;
        }
      },
    });

    if (!foundFragment) {
      throw new Error("Could not find fragment in template component");
    }

    let dummyFilePath;
    if (sourceFilePath) {
      const projectFolder =
        sourceFilePath.split(`/${canvasId}/`)[0] + `/${canvasId}`;
      dummyFilePath = projectFolder ? `${projectFolder}/dummy.tsx` : null;
    } else {
      const importWithAbsolutePath = importsToAdd.find((imp: Import) =>
        imp.filename?.includes(canvasId),
      );

      const projectFolder = importWithAbsolutePath
        ? importWithAbsolutePath?.filename?.split(`/${canvasId}/`)[0] +
          `/${canvasId}`
        : null;
      dummyFilePath = projectFolder ? `${projectFolder}/dummy.tsx` : null;
    }

    importsToAdd.forEach((imp: Import) => {
      ensureImportInAST(
        emptyComponentAST,
        imp.name,
        imp.filename,
        dummyFilePath ?? undefined,
        imp.importType,
        imp.importedName,
      );
    });

    const newCode = print(emptyComponentAST, {
      parser: recastBabelTSParser,
    }).code;
    return {
      newFileContents: newCode,
      dummyFilePath: dummyFilePath,
    };
  } catch (e) {
    console.error(e);
    throw new Error("Could not create file from extracted elements: " + e);
  }
};

/**
 * TO DO: This being in two steps (converting to extracted elements, then to a component)
 * is not necessary and is because this was legacy code that was moved around
 *
 * This can be fixed by just converting the nav tree nodes to a component directly.
 */
export const getComponentFileFromNavTreeNodes = (
  canvasId: string,
  navTreeNodes: NavTreeNode[],
  forDetach?: boolean,
  treeElementOverrides?: { [codebaseId: string]: JsxElement },
): {
  newFileContents: string;
  dummyFilePath: string | null;
  sourceFilePath: string | null;
} | null => {
  if (!navTreeNodes?.length) {
    return null;
  }

  const treeElementLookup = {
    ...useCodebaseStore.getState().treeElementLookup,
    ...treeElementOverrides,
  };

  let error, sourceFilePath;
  const newExtractedElements = navTreeNodes
    .map((navTreeNode) => {
      const {
        extractedElement,
        error: innerError,
        sourceFilePath: innerSourceFilePath,
      } = getExtractedElement(navTreeNode, treeElementLookup, forDetach);
      error = innerError || error;
      sourceFilePath = sourceFilePath || innerSourceFilePath;
      return extractedElement;
    })
    .filter((el) => el !== undefined);

  if (error || !newExtractedElements?.length) {
    console.error("Error extracting elements", error);
    return null;
  }

  try {
    const { newFileContents, dummyFilePath } = constructNewComponent(
      canvasId,
      newExtractedElements,
      sourceFilePath,
    );
    return {
      newFileContents,
      dummyFilePath,
      sourceFilePath,
    };
  } catch (e) {
    console.error("Error constructing new component", e);
    return null;
  }
};

/**
 * For storybook components, returns the key for the component that story is for.
 * For components, returns the key for that component.
 */
export const getLookupKeyForComponent = <
  T extends JsxElement | null | undefined,
>(
  element: T,
): T extends null | undefined ? string | null : string => {
  if (!element) {
    return null as any;
  }

  if (element.type === JsxElementType.StorybookComponent) {
    return [
      element.storybookMainComponentImport?.filename,
      element.storybookMainComponentImport?.importType,
      element.storybookMainComponentImport?.name,
    ].join(":");
  }

  if (element.type === JsxElementType.ComponentInstance) {
    return [
      element.componentSourceImport?.filename,
      element.componentSourceImport?.importType,
      element.componentSourceImport?.name,
    ].join(":");
  }

  if (element.type === JsxElementType.LibraryComponent) {
    return [
      element.libraryImport?.filename,
      element.libraryImport?.importType,
      element.libraryImport?.name,
    ].join(":");
  }

  let fileNameWithoutExtension = element.filename
    ? element.filename.substring(0, element.filename.lastIndexOf("."))
    : element.filename;

  if (fileNameWithoutExtension.endsWith("index")) {
    fileNameWithoutExtension = fileNameWithoutExtension.substring(
      0,
      fileNameWithoutExtension.lastIndexOf("/"),
    );
  }

  const result = [
    fileNameWithoutExtension,
    element.defaultExport
      ? ImportType.ImportDefaultSpecifier
      : ImportType.ImportSpecifier,
    element.componentName,
  ].join(":");

  return result;
};

export const getStoriesForComponent = (
  project: { framework?: ReactFramework },
  treeElementLookup: { [codebaseId: string]: JsxElement },
  component: JsxElement,
) => {
  const componentLookupKey = getLookupKeyForComponent(component);

  return Object.keys(treeElementLookup)
    ?.map((codebaseId: string) => {
      const element: JsxElement = treeElementLookup[codebaseId];

      const filenameWithoutExtension = element.filename.replace(
        /\.[^/.]+$/,
        "",
      );

      if (
        filenameWithoutExtension.includes("tempobook/dynamic") ||
        filenameWithoutExtension.includes("tempobook/storyboards")
      ) {
        return;
      }

      if (
        project?.framework === ReactFramework.NEXT_JS &&
        filenameWithoutExtension.endsWith("pages/_app")
      ) {
        return;
      }

      if (element.type === JsxElementType.StorybookComponent) {
        const key = getLookupKeyForComponent(element);
        if (key === componentLookupKey) {
          return element;
        }
      }
    })
    .filter((element): element is JsxElement => Boolean(element));
};
